import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { Accordion, Paper } from '@material-ui/core';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import { withParentSize } from '@visx/responsive';
import AccountStatus from 'Components/UI/AccountStatus';
import Typography from 'Components/UI/Typography';
import { PortfolioProvider } from 'Components/UI/Portfolio/PortfolioContext';
import { LAYOUTS } from 'Components/UI/Accounts/AccountForm';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import useMetaLayoutContext
  from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import MetaField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { currency } from 'Helpers/Formats';
import Chart
  from 'Components/AdvisorApp/Viewport/MissionControl/Page/Versions/Default/Tabs/PlanningData/Investments/Chart';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

const TypograpgyOrField = ({
  open,
  attrName,
  resource,
  className,
  text,
  ...rest
}) => {
  return (
    <div className={className}>
      {open ? (
        <MetaField
          attr={attrName}
          onClick={e => e.stopPropagation()}
          onFocus={e => e.stopPropagation()}
          size="small"
          {...rest}
        />
      ) : (
        <Typography noWrap color="primary">
          {text || resource.attr(attrName)}
        </Typography>
      )}
    </div>
  );
};

TypograpgyOrField.defaultProps = {
  className: '',
  text: '',
};

TypograpgyOrField.propTypes = {
  className: PropTypes.string,
  open: PropTypes.string.isRequired,
  attrName: PropTypes.string.isRequired,
  resource: PropTypes.object.isRequired,
  text: PropTypes.object,
};

const styles = theme => ({
  root: {
    height: 'auto',
  },
  content: {
    display: 'grid',
    gridTemplate: `
    "form chart"
    `,
    gridTemplateColumns: '4fr 6fr',
    gridTemplateRows: '100%',
  },
  columns: {
    gridTemplate: `
    "form"
    "chart"
    `,
    gridTemplateColumns: '100%',
  },
  accountForm: {
    gridArea: 'form',
    overflow: 'auto',
    borderRadius: theme.radius.sm,
    margin: theme.spacing(1),
    padding: theme.spacing(2),
    minWidth: theme.dimensions.sm,
    maxWidth: '100%',
  },
  text: {
    justifyContent: 'center',
    alignItems: 'center',
    flexBasis: '100%',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    '& > .MuiPaper-root': {
      width: '100%',
    },
  },
  summaryContent: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'space-between',
    textAlign: 'center',
    maxWidth: '100%',
  },
  expanded: {
    backgroundColor: theme.palette.facet.lightGray,
    '& .MuiAccordionSummary-content': {
      margin: '12px 0',
    },
  },
  cell: {
    marginTop: 0,
    fontSize: 12,
    textAlign: 'left',
    flexGrow: 1,
    width: '100%',
    maxWidth: `${(1 / 5) * 100}%`,
    padding: theme.spacing(0, 0.5),
    '& > * label + .MuiInput-formControl': {
      marginTop: 0,
    },
    '& .MuiFormControl-root .MuiInputBase-root > .MuiInputBase-input': {
      fontSize: 12,
    },
  },
  summaryRoot: {
    padding: theme.spacing(0, 1),
    display: 'flex',
  },
  rightContent: {
    display: 'flex',
    flexDirection: 'column',
    gridArea: 'chart',
    maxWidth: '100%',
    minWidth: 400,
  },
  wrap: {
    maxWidth: '100%',
    flexBasis: '100%',
  },
  grey: {
    backgroundColor: theme.palette.facet.lightGray,
  },
  paper: {
    borderRadius: theme.radius.sm,
    margin: theme.spacing(1),
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
  },
  chart: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
});

const usePortfolioRelationship = (householdResource, resource) => React.useMemo(
  () => householdResource.relationship('portfolio')
    .link('related')
    .copy()
    .equal('id', resource.id()),
  [householdResource?.instanceId(), resource?.instanceId()],
);

const AccountAccordionFormContent = withParentSize(
  ({
    classes, parentWidth, resource, children,
  }) => {
    const { householdResource } = useSimpleHousehold();
    const portfolioRel = usePortfolioRelationship(householdResource,
      resource);
    const isSmall = parentWidth <= 600;

    return (
      <div className={classNames(classes.content, {
        [classes.columns]: isSmall,
      })}
      >
        <Paper className={classNames(classes.accountForm)}>
          {children}
        </Paper>
        <div className={classes.rightContent}>
          <Paper className={classNames(classes.chartPaper, classes.paper)}>
            <PortfolioProvider portfolioRelationship={portfolioRel}>
              <Chart
                center={isSmall}
                classes={{
                  root: classNames(classes.chart, {
                    [classes.wrap]: isSmall,
                  }),
                }}
              />
            </PortfolioProvider>
          </Paper>
        </div>
      </div>
    );
  },
);

AccountAccordionFormContent.propTypes = {
  classes: PropTypes.object.isRequired,
  resource: PropTypes.object.isRequired,
};

const getTypePrompt = (resource) => {
  if (!resource) {
    return null;
  }
  const typePromptResource = resource.attr('type');
  const field = resource.meta().field('type');
  return field.options().findItem(i => i.id() === typePromptResource)?.attr('prompt');
};
const AccountAccordionForm = ({
  className, classes, open, toggleOpen,
}) => {
  const { resource } = useMetaLayoutContext();
  const [opening, setOpening] = React.useState(false);
  const PartialLayout = LAYOUTS[resource?.attr('accountType')];
  const partialLayout = <PartialLayout />;

  const toggle = () => {
    if (opening) return;

    if (!open) {
      setOpening(true);
    }
    toggleOpen();
  };

  return (
    <Accordion
      square
      className={classNames(classes.root, className, {
        [classes.grey]: open,
      })}
      onChange={toggle}
      expanded={open}
      TransitionProps={{
        mountOnEnter: true,
        onEntered: () => setOpening(false),
        timeout: { exit: 300, enter: 300 },
      }}
    >
      <AccordionSummary
        classes={{
          root: classes.summaryRoot,
          content: classes.summaryContent,
          expanded: classes.expanded,
        }}
        expandIcon={
          <ExpandMoreIcon />
            }
        IconButtonProps={{ style: { position: 'absolute', right: 16 } }}
      >
        <TypograpgyOrField
          attrName="type"
          className={classes.cell}
          resource={resource}
          open={open}
          SelectProps={{ disableUnderline: true }}
          text={getTypePrompt(resource)}
        />
        <TypograpgyOrField
          attrName="institution"
          className={classes.cell}
          resource={resource}
          open={open}
        />
        <TypograpgyOrField
          attrName="name"
          className={classes.cell}
          resource={resource}
          open={open}
        />
        <TypograpgyOrField
          attrName="balance"
          className={classes.cell}
          resource={resource}
          open={open}
          text={currency(resource.attr('balance'))}
        />
        <div className={classes.cell}>
          <AccountStatus resource={resource} tooltip />
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <AccountAccordionFormContent
          classes={classes}
          resource={resource}
          debounceTime={100}
          ignoreDimensions={['height', 'top', 'left']}
          open={open}
        >
          {partialLayout}
        </AccountAccordionFormContent>
      </AccordionDetails>
    </Accordion>
  );
};

AccountAccordionForm.defaultProps = {
  className: '',
};

AccountAccordionForm.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  toggleOpen: PropTypes.func.isRequired,
};

export default withStyles(styles)(AccountAccordionForm);
